import { message } from "antd";
import { call } from "../../tsrpc";
import {
  CommercialAnalyticsKind,
  CommercialAnalyticsScope,
  ReqCommercialAnalyticsQuery,
  ResCommercialAnalyticsQuery,
} from "../../shared/protocols/editor/PtlCommercialAnalyticsQuery";

export const DAY_MS = 24 * 60 * 60 * 1000;

export interface CommercialCommonFilters {
  rangeDays: number;
  platform?: string;
  userId?: string;
}

export function buildCommercialQuery(
  scope: CommercialAnalyticsScope,
  kind: CommercialAnalyticsKind,
  filters: CommercialCommonFilters,
  extra: Partial<ReqCommercialAnalyticsQuery> = {},
): ReqCommercialAnalyticsQuery {
  const endTime = Date.now();
  return compactQuery({
    timestamp: endTime,
    token: localStorage.getItem("token") || "",
    scope,
    kind,
    startTime: endTime - Math.max(1, Math.min(90, filters.rangeDays)) * DAY_MS,
    endTime,
    platform: filters.platform?.trim(),
    userId: filters.userId?.trim(),
    ...extra,
  });
}

export async function queryCommercialAnalytics(
  request: ReqCommercialAnalyticsQuery,
): Promise<ResCommercialAnalyticsQuery["data"]> {
  const result = await call("editor/CommercialAnalyticsQuery", request);
  if (!result.isSucc) {
    const errorMessage = result.err?.message || "商业化统计查询失败";
    message.error(errorMessage);
    throw new Error(errorMessage);
  }
  return result.res.data;
}

export function formatInteger(value: unknown): string {
  const number = Number(value);
  return Number.isFinite(number) ? Math.round(number).toLocaleString("zh-CN") : "0";
}

export function formatDecimal(value: unknown, digits = 2): string {
  const number = Number(value);
  return Number.isFinite(number) ? number.toFixed(digits).replace(/\.00$/, "") : "0";
}

export function formatPercent(value: unknown): string {
  const number = Number(value);
  return `${(Number.isFinite(number) ? number * 100 : 0).toFixed(1)}%`;
}

export function formatDuration(value: unknown): string {
  const milliseconds = Number(value);
  if (!Number.isFinite(milliseconds) || milliseconds <= 0) return "-";
  if (milliseconds < 1000) return `${Math.round(milliseconds)} ms`;
  return `${(milliseconds / 1000).toFixed(2)} 秒`;
}

export function formatDateTime(value: unknown): string {
  const timestamp = Number(value);
  if (!Number.isFinite(timestamp) || timestamp <= 0) return "-";
  return new Date(timestamp).toLocaleString("zh-CN", { hour12: false });
}

export function displayUser(row: { username?: string; nickname?: string; userId?: string }): string {
  return row.nickname || row.username || row.userId || "-";
}

export function downloadCsv(filename: string, rows: Array<Record<string, unknown>>): void {
  if (!rows.length) {
    message.info("当前没有可导出的数据");
    return;
  }
  const headers = Array.from(new Set(rows.flatMap((row) => Object.keys(row))));
  const lines = [
    headers.map(escapeCsvCell).join(","),
    ...rows.map((row) => headers.map((header) => escapeCsvCell(row[header])).join(",")),
  ];
  const blob = new Blob(["\ufeff", lines.join("\r\n")], { type: "text/csv;charset=utf-8" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
}

export interface DownloadTextOptions {
  bom?: boolean;
}

export function buildDownloadTextContent(content: string, options: DownloadTextOptions = {}): string {
  return options.bom ? `\ufeff${content}` : content;
}

export function downloadTextFile(filename: string, content: string, mime = "text/plain;charset=utf-8", options: DownloadTextOptions = {}): void {
  const blob = new Blob([buildDownloadTextContent(content, options)], { type: mime });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
}

export function optionRows(values: Array<string | number> | undefined) {
  return (values || []).map((value) => ({ label: String(value), value: String(value) }));
}

function compactQuery<T extends Record<string, unknown>>(value: T): T {
  return Object.fromEntries(
    Object.entries(value).filter(([, field]) => field !== undefined && field !== null && field !== ""),
  ) as T;
}

function escapeCsvCell(value: unknown): string {
  const text = value == null
    ? ""
    : typeof value === "object"
      ? JSON.stringify(value)
      : String(value);
  return `"${text.replace(/"/g, '""')}"`;
}
